import EventEmitter from 'events';
import createRateLimitedRunnerDefault from '../../helpers/rateLimitedRunner';

const hasNewVideoInterruption = (prevStats, currentStats) =>
  typeof prevStats?.video?.pauseCount === 'number' &&
  typeof currentStats.video?.pauseCount === 'number' &&
  prevStats.video.pauseCount < currentStats.video.pauseCount;

const hasCodecChanged = (prevStats, currentStats) =>
  prevStats?.video?.codec &&
  currentStats?.video?.codec &&
  prevStats.video.codec !== currentStats.video.codec;

const hasResolutionChanged = (prevStats, currentStats) =>
  prevStats?.video?.width &&
  prevStats.video.height &&
  (prevStats.video.width !== currentStats.video?.width ||
  prevStats.video.height !== currentStats.video?.height);

const getLogPayloadFromStats = (ev) => {
  const videoStats = ev.stats?.video;
  if (!videoStats) return null;
  return {
    codec: videoStats.codec,
    decodedFrameRate: videoStats.decodedFrameRate,
    freezeCount: videoStats.freezeCount,
    height: videoStats.height,
    pauseCount: videoStats.pauseCount,
    totalFreezesDuration: videoStats.totalFreezesDuration,
    totalPausesDuration: videoStats.totalPausesDuration,
    width: videoStats.width,
  };
};

const createSubscriberVideoQualityChangedChecker = (deps) => {
  const { getPcStats, logAnalyticsEvent } = deps;
  const createRateLimitedRunner = deps.createRateLimitedRunner ?? createRateLimitedRunnerDefault;
  const videoQualityChangeLogger = createRateLimitedRunner((videoQualityChangedEvent) => {
    logAnalyticsEvent('videoQualityChanged', 'Event', getLogPayloadFromStats(videoQualityChangedEvent), { reason: videoQualityChangedEvent.reason });
  });
  const ee = new EventEmitter();
  let lastStats;

  const processStats = (stats) => {
    const prevLastStats = lastStats;
    lastStats = stats;
    let event;

    if (hasNewVideoInterruption(prevLastStats, stats)) {
      event = {
        reason: 'videoInterruption',
        stats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
      return;
    }

    if (hasCodecChanged(prevLastStats, stats)) {
      event = {
        reason: 'codecChange',
        stats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
      return;
    }

    if (hasResolutionChanged(prevLastStats, stats)) {
      event = {
        reason: 'resolutionChange',
        stats,
      };
      ee.emit('videoQualityChanged', event);
      videoQualityChangeLogger.run(event);
    }
  };

  const getStats = () => {
    getPcStats((err, stats) => {
      if (!err) {
        processStats(stats);
      }
    });
  };

  return Object.assign(ee, {
    check() {
      getStats();
    },

    destroy() {
      ee.removeAllListeners();
    },
  });
};

export default createSubscriberVideoQualityChangedChecker;
